'use client';

import { useCallback, useMemo, useState } from 'react';
import { Button } from '@savvy-kit/components/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@savvy-kit/components/dialog';
import SearchableCombobox from '@savvy-kit/patterns/searchable-combobox';
import Tag from '@savvy-kit/patterns/tag';
import { tagIndicatorPropsFromTagData } from 'src/utils/advisor-dash/tag-indicator-from-tag-data';

import type { BulkTagInfo } from './bulk-tag-types';

export type BulkManageTagsConfirmPayload = {
  readonly tagsToAdd: readonly string[];
  readonly tagsToRemove: readonly string[];
};

type Props = {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly selectedCount: number;
  readonly availableTags: readonly BulkTagInfo[];
  readonly presentTags: readonly BulkTagInfo[];
  readonly onConfirm: (payload: BulkManageTagsConfirmPayload) => Promise<void>;
  readonly entityNoun: string;
  readonly entityNounPlural: string;
};

export function BulkManageTagsDialog({
  open,
  onOpenChange,
  selectedCount,
  availableTags,
  presentTags,
  onConfirm,
  entityNoun,
  entityNounPlural,
}: Props) {
  const [tagsToAdd, setTagsToAdd] = useState<Set<string>>(new Set());
  const [tagsToRemove, setTagsToRemove] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);

  const addOptions = useMemo(
    () =>
      availableTags
        .filter((t) => !tagsToAdd.has(t.id) && !t.isSmart)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((t) => ({ value: t.id, label: t.name })),
    [availableTags, tagsToAdd],
  );

  const selectedAddTags = useMemo(
    () => availableTags.filter((t) => tagsToAdd.has(t.id)).sort((a, b) => a.name.localeCompare(b.name)),
    [availableTags, tagsToAdd],
  );

  const removableTags = useMemo(
    () => [...presentTags].filter((t) => !t.isSmart).sort((a, b) => a.name.localeCompare(b.name)),
    [presentTags],
  );

  const handleAddTag = useCallback((tagId: string) => {
    if (tagId !== '') setTagsToAdd((prev) => new Set(prev).add(tagId));
  }, []);

  const handleRemoveAddedTag = useCallback((tagId: string) => {
    setTagsToAdd((prev) => {
      const next = new Set(prev);
      next.delete(tagId);
      return next;
    });
  }, []);

  const toggleRemoveTag = useCallback((tagId: string) => {
    setTagsToRemove((prev) => {
      const next = new Set(prev);
      if (next.has(tagId)) next.delete(tagId);
      else next.add(tagId);
      return next;
    });
  }, []);

  const resetState = useCallback(() => {
    setTagsToAdd(new Set());
    setTagsToRemove(new Set());
  }, []);

  const handleConfirm = async () => {
    setIsSaving(true);
    try {
      await onConfirm({
        tagsToAdd: Array.from(tagsToAdd),
        tagsToRemove: Array.from(tagsToRemove),
      });
      resetState();
      onOpenChange(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) resetState();
    onOpenChange(nextOpen);
  };

  const hasChanges = tagsToAdd.size > 0 || tagsToRemove.size > 0;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="bg-popover" initialFocus={false}>
        <DialogTitle className="text-foreground">Manage tags</DialogTitle>
        <DialogDescription className="text-secondary">
          Add or remove tags on {selectedCount} selected {selectedCount === 1 ? entityNoun : entityNounPlural}.
        </DialogDescription>

        <div className="mt-3 grid gap-4">
          <div className="grid gap-2">
            <p className="text-foreground text-sm font-medium">Add tags</p>
            <SearchableCombobox
              id="bulk-manage-tag-add-select"
              options={addOptions}
              value=""
              onValueChange={handleAddTag}
              placeholder="Search tags..."
              emptyMessage="No tags found"
            />
            {selectedAddTags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {selectedAddTags.map((t) => (
                  <Tag
                    key={t.id}
                    {...tagIndicatorPropsFromTagData(t)}
                    type="filter"
                    dismissible
                    onDismiss={() => handleRemoveAddedTag(t.id)}
                    className="rounded-sm"
                  >
                    {t.name}
                  </Tag>
                ))}
              </div>
            )}
          </div>

          <div className="grid gap-2">
            <p className="text-foreground text-sm font-medium">Remove tags</p>
            {removableTags.length === 0 ? (
              <p className="text-secondary text-sm">No removable tags on selected {entityNounPlural}.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {removableTags.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => toggleRemoveTag(t.id)}
                    type="button"
                    aria-pressed={tagsToRemove.has(t.id)}
                    className="inline-block"
                  >
                    <Tag
                      {...tagIndicatorPropsFromTagData(t)}
                      type="selectable"
                      className={`rounded-sm hover:cursor-pointer ${tagsToRemove.has(t.id) ? 'text-primary border-primary' : ''}`}
                    >
                      {t.name}
                    </Tag>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <DialogClose
            render={
              <Button variant="secondary" disabled={isSaving}>
                Cancel
              </Button>
            }
          />
          <Button onClick={handleConfirm} disabled={isSaving || !hasChanges}>
            {isSaving ? 'Saving...' : 'Apply changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
